import { useState } from 'react';
import {
  FaCheckCircle,
  FaQuestionCircle,
  FaExclamationCircle,
} from 'react-icons/fa';
import { HiOutlineLightningBolt } from 'react-icons/hi';
import { toast } from 'react-hot-toast';

import {
  addJiraComment,
  deleteJiraComment,
  updateJiraComment,
} from '../../services/api';
import { DataState } from '../../context/DataProvider';

function convertToIST(dateString) {
  // Parse the date string into a Date object (in UTC)
  const dateUTC = new Date(dateString);

  // Format the date to "28 Jan, 2025"
  const formattedDate = dateUTC.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });

  // Format the time to "7:20 AM"
  const formattedTime = dateUTC.toLocaleTimeString('en-IN', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });

  return { date: formattedDate, time: formattedTime };
}

const JiraIssueActivity = ({
  issue_key,
  comments,
  allAssignees,
  refreshIssue,
}) => {
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editedCommentBody, setEditedCommentBody] = useState('');
  const [activeTab, setActiveTab] = useState('Comments'); // Default to "Comments"
  const [textareaValue, setTextareaValue] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [savecommentloading, setSavecommentloading] = useState(false);
  const [deletecommentLoading, setDeletecommentLoading] = useState({});
  const [editcommentSaveLoading, setEditCommentSaveLoading] = useState({});
  const { userGoogleId } = DataState();

  const handleEditCommentCancel = () => {
    // Reset the editing states to cancel editing
    setEditingCommentId(null);
    setEditedCommentBody('');
  };

  const startEditing = (comment) => {
    setEditingCommentId(comment.id);
    setEditedCommentBody(comment.body);
  };

  const handleTextareaChange = (e) => {
    setTextareaValue(e.target.value);
    setIsEditing(e.target.value.trim() !== ''); // Show buttons only when there is text
  };

  const handleButtonClick = (text) => {
    setTextareaValue(text);
    setIsEditing(true); // Show Save & Cancel when a button is clicked
  };

  const handleSaveComment = async () => {
    if (textareaValue.trim()) {
      setSavecommentloading(true);

      try {
        // const comment_data_body = {"comment_body":textareaValue.trim()};
        const commentData = {
          body: {
            type: 'doc',
            version: 1,
            content: [
              {
                type: 'paragraph',
                content: [
                  {
                    text: textareaValue.trim(),
                    type: 'text',
                  },
                ],
              },
            ],
          },
        };

        await addJiraComment(issue_key, commentData, userGoogleId);

        toast.success('Comment saved successfully!');

        setTextareaValue('');
        setIsEditing(false);
        refreshIssue();
      } catch (error) {
        toast.error('Failed to save comment.');
      } finally {
        setSavecommentloading(false);
      }
    }
  };

  const handleCommentDelete = async (commentId) => {
    setDeletecommentLoading((prevState) => ({
      ...prevState,
      [commentId]: true, // Set loading state for the specific comment
    }));

    try {
      await deleteJiraComment(issue_key, commentId, userGoogleId);

      toast.success('Comment deleted successfully!');

      refreshIssue();
    } catch (error) {
      toast.error('Failed to delete comment.');
    } finally {
      setDeletecommentLoading((prevState) => ({
        ...prevState,
        [commentId]: false, // Reset loading state for the specific comment
      }));
    }
  };

  const saveEditedComment = async (commentId) => {
    if (editedCommentBody.trim()) {
      setEditCommentSaveLoading((prevState) => ({
        ...prevState,
        [commentId]: true,
      }));

      try {
        await updateJiraComment(
          issue_key,
          commentId,
          editedCommentBody.trim(),
          userGoogleId,
        );

        toast.success('Comment updated successfully!');

        setEditedCommentBody('');
        setEditingCommentId(null);
        refreshIssue();
      } catch (error) {
        toast.error('Error updating comment!');
      } finally {
        setEditCommentSaveLoading((prevState) => ({
          ...prevState,
          [commentId]: false,
        }));
      }
    }
  };

  const handleCancelComment = () => {
    setTextareaValue('');
    setIsEditing(false);
  };

  return (
    <>
      <h3 className="font-semibold text-gray-800">Activity</h3>
      {/* Toggle Buttons */}
      <div className="mb-4 mt-2 flex w-fit space-x-1 rounded-md border border-gray-300 p-1">
        {['All', 'Comments', 'History'].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`rounded-md px-4 py-1 text-xs font-medium transition-all 
                ${activeTab === tab ? 'border border-[#5652CF] bg-blue-100 text-[#5652CF]' : ' border border-white text-gray-700 hover:border-gray-200 hover:bg-gray-200'}
                `}
          >
            {tab}
          </button>
        ))}
      </div>

      <div>
        {activeTab === 'All' && (
          <div>
            <h3 className="mb-2 text-sm font-semibold text-gray-700">
              All Activity
            </h3>
            <div className="comment-section rounded-lg">
              {comments?.map((comment) => {
                const author = allAssignees.find(
                  (user) => user.accountId === comment.author.accountId,
                );
                const avatarUrl = author ? author.avatarUrls?.['16x16'] : null;

                return (
                  <div
                    key={comment.id}
                    className="comment mb-4 flex rounded-lg border border-gray-200 bg-white p-3 shadow-sm"
                  >
                    {avatarUrl && (
                      <img
                        src={avatarUrl}
                        alt={`${comment.author.displayName}'s avatar`}
                        className="mr-3 h-8 w-8 rounded-full"
                      />
                    )}
                    <div>
                      {/* Comment Header */}
                      <div className="comment-header flex items-center font-semibold text-gray-700">
                        {comment.author.displayName}
                        {/* <span className="text-gray-500 text-sm ml-2">• {convertToIST(comment.created).date} {convertToIST(comment.created).time}</span> */}
                        {(() => {
                          const commentDateTime = convertToIST(comment.created);
                          return (
                            <span className="ml-2 text-sm text-gray-500">
                              • {commentDateTime.date} {commentDateTime.time}
                            </span>
                          );
                        })()}
                      </div>

                      {/* Editable Comment Body */}
                      <div className="comment-body mt-2 text-gray-800">
                        {editingCommentId === comment.id ? (
                          <textarea
                            className="w-full rounded-md border border-gray-300 p-2 text-gray-700"
                            value={editedCommentBody}
                            onChange={(e) =>
                              setEditedCommentBody(e.target.value)
                            }
                          />
                        ) : (
                          <p>{comment.body}</p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {activeTab === 'Comments' && (
          <div>
            <div className="flex flex-col rounded-lg border border-gray-300 ">
              <textarea
                id="textarea"
                rows="5"
                value={textareaValue}
                onChange={handleTextareaChange}
                placeholder="Write your comment here..."
                className="scrollbar-none w-full resize-none rounded-lg p-3 text-sm text-gray-800 focus:outline-none focus:ring-1 focus:ring-gray-400"
              ></textarea>
              {isEditing && (
                <div className="mb-1 mt-1 flex items-center justify-end gap-2 p-1 text-xs ">
                  <button
                    className="rounded-md bg-blue-600 px-3 py-1 font-medium  text-white"
                    onClick={handleSaveComment}
                    disabled={savecommentloading}
                  >
                    {savecommentloading ? (
                      <span className="loader">Saving...</span>
                    ) : (
                      'Save'
                    )}
                  </button>
                  <button
                    className="rounded-md bg-gray-400 px-3 py-1 font-medium text-white"
                    onClick={handleCancelComment}
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>

            <div className="mb-4 mt-2 flex flex-wrap items-center gap-2">
              <button
                className="flex items-center gap-1 rounded-full bg-green-100 px-3 py-1 text-xs font-medium text-green-700"
                onClick={() => handleButtonClick('✔️ Looks good!')}
              >
                <FaCheckCircle /> Looks good!
              </button>
              <button
                className="flex items-center gap-1 rounded-full bg-yellow-100 px-3 py-1 text-xs font-medium text-yellow-700"
                onClick={() => handleButtonClick('❓ Need help?')}
              >
                <FaQuestionCircle /> Need help?
              </button>
              <button
                className="flex items-center gap-1 rounded-full bg-red-100 px-3 py-1 text-xs font-medium text-red-700"
                onClick={() => handleButtonClick('⚠️ This is blocked...')}
              >
                <FaExclamationCircle /> This is blocked...
              </button>
              <button
                className="flex items-center gap-1 rounded-full bg-blue-100 px-3 py-1 text-xs font-medium text-blue-700"
                onClick={() => handleButtonClick('⚡ Can you clarify...?')}
              >
                <HiOutlineLightningBolt /> Can you clarify...?
              </button>
              <button
                className="flex items-center gap-1 rounded-full bg-green-100 px-3 py-1 text-xs font-medium text-green-700"
                onClick={() => handleButtonClick('✔️ This is on track')}
              >
                <FaCheckCircle /> This is on track
              </button>
            </div>

            <div className="comment-section rounded-lg">
              {comments?.map((comment) => {
                const author = allAssignees.find(
                  (user) => user.accountId === comment.author.accountId,
                );
                const avatarUrl = author ? author.avatarUrls?.['16x16'] : null;

                return (
                  <div
                    key={comment.id}
                    className="comment mb-4 flex rounded-lg border border-gray-200 bg-white p-3 shadow-sm"
                  >
                    {avatarUrl && (
                      <img
                        src={avatarUrl}
                        alt={`${comment.author.displayName}'s avatar`}
                        className="mr-3 h-8 w-8 rounded-full"
                      />
                    )}
                    <div>
                      {/* Comment Header */}
                      <div className="comment-header flex items-center font-semibold text-gray-700">
                        {comment.author.displayName}
                        {/* <span className="text-gray-500 text-sm ml-2">• {convertToIST(comment.created).date} {convertToIST(comment.created).time}</span> */}
                        {(() => {
                          const commentDateTime = convertToIST(comment.created);
                          return (
                            <span className="ml-2 text-sm text-gray-500">
                              • {commentDateTime.date} {commentDateTime.time}
                            </span>
                          );
                        })()}
                      </div>

                      {/* Editable Comment Body */}
                      <div className="comment-body mt-2 text-gray-800">
                        {editingCommentId === comment.id ? (
                          <textarea
                            className="scrollbar-none w-full rounded-md border border-gray-300 p-2 text-gray-700 focus:outline-none focus:ring-1 "
                            value={editedCommentBody}
                            onChange={(e) =>
                              setEditedCommentBody(e.target.value)
                            }
                          />
                        ) : (
                          <p>{comment.body}</p>
                        )}
                      </div>

                      {/* Action Buttons */}
                      <div className="mt-2 flex items-center space-x-2">
                        {editingCommentId === comment.id ? (
                          <>
                            <button
                              onClick={() => saveEditedComment(comment.id)}
                              className="  text-sm text-gray-400 hover:text-gray-700 hover:underline"
                              disabled={editcommentSaveLoading[comment.id]}
                            >
                              {editcommentSaveLoading[comment.id] ? (
                                <span className="loader">Saving...</span> // Show loader if saving
                              ) : (
                                '• Save'
                              )}
                            </button>
                            <button
                              onClick={() => handleEditCommentCancel()}
                              className=" text-sm text-gray-400 hover:text-gray-700 hover:underline"
                            >
                              • cancel
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              onClick={() => startEditing(comment)}
                              className=" text-sm text-gray-400 hover:text-gray-700 hover:underline"
                            >
                              • Edit
                            </button>
                            <button
                              onClick={() => handleCommentDelete(comment.id)}
                              className=" text-sm text-gray-400 hover:text-gray-700 hover:underline"
                              disabled={deletecommentLoading[comment.id]}
                            >
                              {deletecommentLoading[comment.id] ? (
                                <span className="loader">Deleting...</span>
                              ) : (
                                '• Delete'
                              )}
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {activeTab === 'History' && (
          <div>
            <h3 className="text-sm font-semibold text-gray-700">History</h3>
            <ul className="text-gray-600">
              <p>No history available.</p>
            </ul>
          </div>
        )}
      </div>
    </>
  );
};

export default JiraIssueActivity;
